import React from 'react'
import { Box, Text, Space, Tooltip, Paper } from '@mantine/core'
import { TextHeading, Icon } from '@/components'
import { useBurn } from './BurnProvider'
import { formatPercentage, formatStatPercentage, formatStrd } from './general'
import { AnimatePresence, motion } from 'framer-motion'

export const BurnResults: React.FC = () => {
  const { results } = useBurn()

  return (
    <Paper
      pt="xl"
      sx={(t) => ({
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        backgroundColor: t.colors.gray[9],
        height: 400,
        overflow: 'hidden'
      })}>
      <TextHeading align="center" sx={(t) => ({ color: t.colors.pink[9], fontSize: 48 })}>
        Your results
      </TextHeading>

      <Space h="md" />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 8, columnGap: 8, height: '100%' }} px="sm">
        {/* STRD Percentage */}
        <Box sx={{ display: 'grid', placeItems: 'center' }}>
          <Box>
            <Text
              align="center"
              weight={600}
              sx={(t) => ({
                fontFamily: t.other.monoFontFamily,
                fontSize: 12,
                color: t.colors.pink[6],
                lineHeight: 1,
                textTransform: 'uppercase',
                height: 12
              })}>
              New % ownership of STRD
            </Text>
            <Space h="sm" />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 36 }}>
              <TextHeading
                align="center"
                sx={(t) => ({
                  fontSize: results.yourStrdInBips ? 20 : 36,
                  lineHeight: 1,
                  color: t.colors.pink[6]
                })}>
                {formatStatPercentage(results.yourStrdPercent)}
              </TextHeading>

              {results.yourStrdPercentDelta > 0 ? (
                <Text
                  weight={700}
                  sx={(t) => ({
                    fontSize: 11,
                    color: t.colors.green[6],
                    lineHeight: 1,
                    transform: results.yourStrdInBips ? 'translateY(-2px)' : 'translateY(-3px)'
                  })}>
                  +{formatPercentage(results.yourStrdPercentDelta)}
                </Text>
              ) : null}
            </Box>
          </Box>
        </Box>

        {/* STRD Token Count */}
        <Box sx={{ display: 'grid', placeItems: 'center' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 12 }}>
              <Text
                align="center"
                weight={600}
                sx={(t) => ({
                  fontFamily: t.other.monoFontFamily,
                  fontSize: 12,
                  color: t.colors.pink[6],
                  lineHeight: 1,
                  textTransform: 'uppercase'
                })}>
                Your STRD Tokens
              </Text>

              <Tooltip
                styles={(t) => ({
                  body: { color: t.colors.pink[6], backgroundColor: 'white' }
                })}
                label={`You hold ${formatStrd(results.yourStrd)} out of ${formatStrd(results.totalStrdRemaining)} STRD remaining`}>
                <Box sx={{ transform: 'translateY(0px)' }}>
                  <Icon name="infoCircle" sx={(t) => ({ color: t.colors.pink[9] })} size={12} />
                </Box>
              </Tooltip>
            </Box>

            <Space h="sm" />

            <TextHeading
              align="center"
              sx={(t) => ({
                fontSize: 36,
                lineHeight: 1,
                color: t.colors.pink[6]
              })}>
              {formatStrd(results.yourStrd)}
            </TextHeading>
          </Box>
        </Box>

        {/* STRD-R Percentage */}
        <Box sx={{ display: 'grid', placeItems: 'center' }}>
          <Box>
            <Text
              align="center"
              weight={600}
              sx={(t) => ({
                fontFamily: t.other.monoFontFamily,
                fontSize: 12,
                color: t.colors.pink[6],
                lineHeight: 1,
                textTransform: 'uppercase',
                height: 12
              })}>
              New % ownership of STRD-R
            </Text>
            <Space h="sm" />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 36 }}>
              <TextHeading
                align="center"
                sx={(t) => ({
                  fontSize: results.yourStrdRInBips ? 20 : 36,
                  lineHeight: 1,
                  color: t.colors.pink[6]
                })}>
                {formatStatPercentage(results.yourStrdRPercent)}
              </TextHeading>
            </Box>
          </Box>
        </Box>

        {/* STRD-R Token Count */}
        <Box sx={{ display: 'grid', placeItems: 'center' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 12 }}>
              <Text
                align="center"
                weight={600}
                sx={(t) => ({
                  fontFamily: t.other.monoFontFamily,
                  fontSize: 12,
                  color: t.colors.pink[6],
                  lineHeight: 1,
                  textTransform: 'uppercase'
                })}>
                Your STRD-R Tokens
              </Text>

              <Tooltip
                styles={(t) => ({
                  body: { color: t.colors.pink[6], backgroundColor: 'white' }
                })}
                label={`You receive ${formatStrd(results.yourStrdR)} out of ${formatStrd(results.totalStrdRMinted)} STRD-R minted`}>
                <Box sx={{ transform: 'translateY(1px)' }}>
                  <Icon name="infoCircle" sx={(t) => ({ color: t.colors.pink[9] })} size={12} />
                </Box>
              </Tooltip>
            </Box>
            <Space h="sm" />
            <TextHeading
              align="center"
              sx={(t) => ({
                fontSize: 36,
                lineHeight: 1,
                color: t.colors.pink[6]
              })}>
              {formatStrd(results.yourStrdR)}
            </TextHeading>
          </Box>
        </Box>
      </Box>

      <Space h="md" />

      <BurnFooterNote />
    </Paper>
  )
}

const ease = [0.19, 1, 0.22, 1] as const

const BurnFooterNote: React.FC = () => {
  const { results } = useBurn()
  const stateKey = results.wasAdjusted
    ? 'adjusted'
    : results.yourStrdInBips || results.yourStrdRInBips
      ? 'bips-info'
      : 'spacer'

  return (
    <Box py="sm" px="lg">
      <AnimatePresence initial={false}>
        <motion.div
          key={stateKey}
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'tween', duration: 0.15, ease }}
          style={{ height: 12 }}>
          {stateKey === 'adjusted' ? (
            <Text
              size="xs"
              align="center"
              sx={(t) => ({
                color: t.colors.yellow[7],
                lineHeight: 1,
                fontSize: 11,
                fontStyle: 'italic',
                fontFamily: t.other.monoFontFamily,
                textTransform: 'uppercase'
              })}>
              * Network burn adjusted up to include your burn.
            </Text>
          ) : stateKey === 'bips-info' ? (
            <Text
              size="xs"
              align="center"
              sx={(t) => ({
                color: t.colors.pink[9],
                lineHeight: 1,
                fontSize: 11,
                fontFamily: t.other.monoFontFamily,
                textTransform: 'uppercase'
              })}>
              * 0.1 bip = 0.001%
            </Text>
          ) : null}
        </motion.div>
      </AnimatePresence>
    </Box>
  )
}
